import { api } from './client';
import type { DashboardData, DashboardPeriod, Order, ShippingMethod, ShirtCut, Size } from '../types';
import type { SelectedPaczkomat } from '../util/paczkomat';

export type CreateOrderItem = {
  product_id: string;
  cut: ShirtCut;
  size: Size;
  qty: number;
};

export type CreateOrderShippingAddress = {
  paczkomat?: SelectedPaczkomat;
  courier?: { street?: string; postalCode?: string; city?: string };
  /** Posted as a bare ISO code; the API resolves the country name it stores. */
  international?: { country: string; countryName?: string };  pickupNote?: string;
};

export type CreateOrderPayload = {
  customerName: string;
  email: string;
  phone: string;
  externalOrderId: string;
  firstName: string;
  items: CreateOrderItem[];
  /** Omitted by a ride-along: the server takes the method and address from the parcel it joins. */
  shipping_method?: ShippingMethod;
  shipping_address: CreateOrderShippingAddress;
  customer_comment_topic?: string | null;
  customer_comment_text?: string | null;
};

export type OrderSummary = {
  id: string;
  status: string;
  customerName: string;
  /** Null for a manual order taken without an address. */
  email: string | null;
  phone: string;
  externalOrderId: string | null;
  shippingMethod: ShippingMethod;
  shippingAddress: CreateOrderShippingAddress;
  shippingCostGrosze: number;
  itemsTotalGrosze: number;
  totalGrosze: number;
  isManual: boolean;
  mergedIntoId: string | null;
  /** Set while this order is waiting to be packed into another order's parcel. */
  shipsWithId: string | null;
  packageSize: string | null;
  trackingNumber: string | null;
  paymentProvider: string | null;
  customerCommentTopic: string | null;
  customerCommentText: string | null;
  createdAt: string;
  paidAt: string | null;
  shippedAt: string | null;
  cancelledAt: string | null;
  items: Array<{
    productId: string;
    cut: ShirtCut;
    size: Size;
    qty: number;
    unitPriceGrosze: number;
  }>;
};

export type UndeliveredEmail = {
  at: string;
  recipient: string;
  template: string | null;
  bounceType: string | null;
  bounceSubType: string | null;
  diagnostic: string | null;
};

export type AdminOrderRow = Omit<OrderSummary, 'email' | 'items'> & {
  /** As stored, including the placeholder of a manual order taken without an address. */
  email: string;
  internalNote: string | null;
  /** Latest SES bounce of the current address; set means the buyer is not receiving order mail. */
  undeliveredEmail?: UndeliveredEmail | null;
  /** The main's status for a merged-in order, which travels and is reported with the main. */
  effectiveStatus?: string;
  items: Array<{
    id: number;
    productId: string;
    cut: ShirtCut;
    size: Size;
    qty: number;
    unitPriceGrosze: number;
  }>;
};

export type LogisticsQueueItem = {
  productId: string;
  color: string;
  cut: ShirtCut;
  size: Size;
  qty: number;
  /** Set when the shirt comes from an order merged into this one. */
  fromOrderId: string | null;
};

export type LogisticsQueueOrder = {
  id: string;
  customerName: string;
  phone: string;
  externalOrderId: string | null;
  shippingMethod: ShippingMethod;
  shippingAddress: CreateOrderShippingAddress;
  trackingNumber: string | null;
  packageSize: string | null;
  customerCommentTopic: string | null;
  customerCommentText: string | null;
  isManual: boolean;
  mergedIntoId: string | null;
  /** Unpaid orders bound to travel in this parcel; their shirts are not in the box. */
  unpaidRideAlongs: string[];
  createdAt: string;
  items: LogisticsQueueItem[];
};

export const listOrders = (params: Record<string, string | undefined> = {}) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const q = search.toString();
  return api.get<{ orders: AdminOrderRow[]; total: number }>(`/admin/orders${q ? `?${q}` : ''}`);
};

export const getOrder = (id: string, token: string) =>
  api.get<OrderSummary>(`/orders/${id}?t=${encodeURIComponent(token)}`);

export const lookupOrder = (orderId: string, surname: string) =>
  api.post<{ id: string; accessToken: string }>('/orders/lookup', { orderId, surname });

export const getAdminOrder = (id: string) =>
  api.get<{ order: AdminOrderRow; items: AdminOrderRow['items'] }>(`/admin/orders/${id}`);

export const createOrder = (payload: CreateOrderPayload) =>
  api.post<{ id: string; accessToken: string }>('/orders', payload);

export type ManualOrderPayload = {
  customerName: string;
  /** Omitted when the buyer gave none; the API stores a placeholder that is never mailed. */
  email?: string;
  phone: string;
  items: CreateOrderItem[];
  shipping_method: ShippingMethod;
  shipping_address: CreateOrderShippingAddress;
  internal_note: string;
  initial_status: 'awaiting_payment' | 'paid' | 'pickup';
  payment_channel?: 'cash' | 'manual';
  send_payment_link?: boolean;
};

export type PaymentLinkDelivery = { ok: true; recipient: string } | { ok: false; error: string };

export const createManualOrder = (payload: ManualOrderPayload) =>
  api.post<{ ok: true; id: string; paymentLinkEmail: PaymentLinkDelivery | null }>(
    '/admin/orders/manual',
    payload,
  );

/** `email` is null when the order has no real address to mail the link to. */
export const getPaymentLink = (id: string) =>
  api.get<{ url: string; email: string | null }>(
    `/admin/orders/${encodeURIComponent(id)}/payment-link`,
  );

export const sendPaymentLink = (id: string) =>
  api.post<{ ok: true; recipient: string }>(
    `/admin/orders/${encodeURIComponent(id)}/payment-link/send`,
    {},
  );

export const updateOrder = (id: string, body: Partial<Order>) =>
  api.patch<Order>(`/orders/${id}`, body);

export const cancelOrders = (ids: string[], reason: string, refundChannelNote = '') =>
  api.post<{ ok: true; ids: string[] }>('/admin/orders/cancel', {
    ids,
    reason,
    refundChannelNote,
  });

export const cancelOrder = (id: string, reason: string, refundChannelNote: string) =>
  api.post<{ order: OrderSummary }>(`/admin/orders/${id}/cancel`, { reason, refundChannelNote });

export const markPaid = (id: string, reason?: string) =>
  api.post<{ order: OrderSummary }>(`/admin/orders/${id}/mark-paid`, { reason });

export const markPaidBulk = (ids: string[], note?: string) =>
  api.post<{ ok: true; ids: string[] }>('/admin/orders/mark-paid', { ids, note });

export const mergeOrders = (mainId: string, mergeIds: string[], note?: string, force?: boolean) =>
  api.post<{ ok: true; mainId: string; mergedIds: string[] }>('/admin/orders/merge', {
    mainId,
    mergeIds,
    note,
    force,
  });

export const unmergeOrder = (id: string, reason?: string) =>
  api.post<{ order: OrderSummary }>(`/admin/orders/${id}/unmerge`, { reason });

export type OrderItemEdit = { id: number; product_id: string; cut: ShirtCut; size: Size };

/** Swaps colour, cut or size per line; quantity, price and totals stay as placed. */
export const editOrderItems = (id: string, items: OrderItemEdit[], reason?: string) =>
  api.put<{ ok: true; id: string; changed: number }>(
    `/admin/orders/${encodeURIComponent(id)}/items`,
    { items, reason },
  );

/** Books a foreign parcel's carrier number, which is what lets it into the kiosk queue. */
export const setTrackingNumber = (id: string, trackingNumber: string) =>
  api.put<{ ok: true; id: string; trackingNumber: string }>(
    `/admin/orders/${id}/tracking-number`,
    { trackingNumber },
  );

export const designatePickup = (id: string) =>
  api.post<{ order: OrderSummary }>(`/admin/orders/${id}/designate-pickup`, {});

export const createStripeSession = (id: string, token: string) =>
  api.post<{ id: string; url: string }>(
    `/orders/${id}/stripe-session?t=${encodeURIComponent(token)}`,
    {},
  );

export type ShipPayload =
  | { method: 'paczkomat'; size: 'A' | 'B' | 'C'; mode: 'shipx' }
  | { method: 'kurier'; size: 'A' | 'B' | 'C' | 'D'; mode: 'shipx' }
  | {
      method: 'paczkomat' | 'kurier';
      mode: 'manual';
      trackingNumber: string;
      reason?: string;
    }
  /** A foreign parcel ships under the shop's own label, with the carrier number stored on it. */
  | { method: 'zagranica'; mode: 'own_label' };

export const shipOrder = (id: string, payload: ShipPayload) =>
  api.post<{ ok: true; id: string; trackingNumber: string; labelUrl?: string }>(
    `/admin/orders/${id}/ship`,
    payload,
  );

/** Same-origin proxy for the InPost A6 label PDF; needs an operator session. */
export const labelUrlFor = (orderId: string) =>
  `/api/admin/orders/${encodeURIComponent(orderId)}/label`;

export const getDashboard = (period: DashboardPeriod = 'week') =>
  api.get<DashboardData>(`/admin/dashboard?period=${encodeURIComponent(period)}`);

export const getLogisticsQueue = () =>
  api.get<{
    queue: LogisticsQueueOrder[];
    cursor: string | null;
    total: number;
    sentToday: number;
  }>('/admin/logistics/queue');
